/* eslint-disable react/jsx-indent */
import React from 'react';
import Moment from 'moment';
import PropTypes from 'prop-types';
import Avatar from '@mui/material/Avatar';
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import LocationOn from '@mui/icons-material/LocationOn';
import AccountBox from '@mui/icons-material/AccountBox';
import Info from '@mui/icons-material/Info';
import { useSelector } from 'react-redux';

import {
  Block,
  Group,
  NoAccounts,
} from '@mui/icons-material';
import {
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import ProfileCardLoading from './components/ProfileCardLoading';

import NoteItem from '../../../../../components/NoteItem';
import { blueGrey } from '../../../../../utils/brandColors';
import HorseLodgingListItem from '../LodgingsListItem';
import LocationChip from '../../../../../components/LocationChip';
import User from '../../../../../components/User';
import { getColorForImportance } from '../../../../../utils/enumHelper';

const ProfileCardComponent = ({
  id,
  openAddEditNoteModal,
  currentHorseLocations,
  horseOwners,
  setTab,
  setOpenEditMode,
  condensed,
}) => {
  // Redux state
  const horse = useSelector((state) => state.horse.horse);
  const currentHorseLodging = useSelector((state) => state.horse.currentHorseLodging);
  const isFetchingHorse = useSelector((state) => state.horse.isFetchingHorse);
  const isFetchingHorseLodgings = useSelector((state) => state.horse.isFetchingHorseLodgings);
  const permissions = useSelector((state) => state.user.permissions);

  // React state
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [defaultOpenLodging, setDefaultOpenLodging] = React.useState(false);

  React.useEffect(() => {
    setDefaultOpenLodging(false);
  }, [id]);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  return (
    isFetchingHorse
      ? <ProfileCardLoading />
      : (
        <Box>
          {currentHorseLodging.inactive && (
            <Box
              sx={{
                p: 2,
                backgroundColor: blueGrey[50],
                borderRadius: '8px 8px 0 0',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Info color="primary" sx={{ mr: 2 }} />
              <Typography fontWeight={600}>INACTIVE since {Moment(currentHorseLodging.startDate).format('ll')}</Typography>
            </Box>
          )}
          {
            horse.activeNote && (
              <Alert
                icon={false}
                variant="outlined"
                severity={getColorForImportance(horse.activeNote.importance)}
                sx={{
                  bgcolor: 'background.paper',
                  width: '100%',
                  flex: 1,
                  paddingRight: 0,
                  '& .MuiAlert-message': { flex: 1 },
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    flex: 1,
                  }}
                >
                  <NoteItem
                    divider={false}
                    pinnedHorseNoteId={horse.activeNote.id}
                    note={horse.activeNote}
                    horseId={horse.id}
                    includeUserInfo={false}
                    condensed
                    onEdit={() => openAddEditNoteModal(horse.activeNote, '', null)}
                  />
                </Box>
              </Alert>
            )
          }
          <Paper
            sx={horse.activeNote
              ? {
                borderRadius: '0 0 12px 16px',
                p: 2,
              }
              : {
                borderRadius: 2,
                p: 2,
              }}
          >
            <Grid container alignItems="flex-start" justifyContent="space-between" spacing={1}>
              <Grid item xs={11}>
                <Grid
                  container
                  alignItems="center"
                  spacing={2}
                >
                  <Grid item>
                    <Avatar
                      sx={{
                        width: condensed
                          ? 40
                          : 100,
                        height: condensed
                          ? 40
                          : 100,
                      }}
                      alt={horse.name}
                      src={`https://${horse.picture
                        ? `${horse.picture}?cacheBust=${new Date()}`
                        : `i0.wp.com/cdn.auth0.com/avatars/${((horse && horse.name) || 'A').charAt(0).toLowerCase()}.png`}`}
                      aria-label={`${horse.name} avatar`}
                    />
                  </Grid>
                  <Grid item>
                    <Typography
                      variant={condensed
                        ? 'h4'
                        : 'h3'}
                    >{horse.name}
                    </Typography>
                    <Typography
                      variant={condensed
                        ? 'h5'
                        : 'h4'}
                      sx={{ fontWeight: 300 }}
                    >{horse.showName || ''}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={condensed
                      ? 12
                      : undefined}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        pb: 1,
                        cursor: setTab
                          ? 'pointer'
                          : 'inherit',
                      }}
                      onClick={() => {
                        if (setTab) {
                          setTab(4)
                        }
                      }}
                    >
                      <LocationOn color="primary" sx={{ mr: 2 }} />
                      {
                        (currentHorseLocations && currentHorseLocations.length > 0) && currentHorseLocations.map((hl) => (
                          <LocationChip key={`horse-location-${hl.id}`} location={hl.location} id={`horse-location-${hl.id}`} />
                        ))
                      }
                      {
                        (!currentHorseLocations || currentHorseLocations.length === 0) && (<Typography>No Location</Typography>)
                      }
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      onClick={() => {
                        if (setTab) {
                          setTab(5)
                        }
                      }}
                    >
                      <AccountBox color="primary" sx={{ mr: 2 }} />
                      <Box
                        sx={{
                          display: 'flex',
                          flex: 1,
                          gap: 1,
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          cursor: setTab
                            ? 'pointer'
                            : 'inherit',
                        }}
                        onClick={() => {
                          if (setTab) {
                            setTab(5)
                          }
                        }}
                      >
                        {
                          (horseOwners && horseOwners.length > 0) && (horseOwners.map((ha) => (
                            <Box
                              sx={{
                                maxWidth: horseOwners.length > 1
                                  ? 165
                                  : '100%',
                                overflow: 'hidden',
                              }}
                              key={`horse-owner-${ha.id}`}
                            ><User user={ha.user} secondary="owner" />
                            </Box>
                          ))
                          )
                        }
                        {
                          (!horseOwners || horseOwners.length === 0) && (<Chip size="small" icon={<NoAccounts color="secondary" />} label="No Owner" />)
                        }
                      </Box>
                    </Box>
                  </Grid>

                  {
                    !condensed && (
                      <Grid
                        item
                        sx={{
                          flex: 1,
                          display: 'flex',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <HorseLodgingListItem border id={id} horseLodging={currentHorseLodging} isAdmin={false} defaultOpen={defaultOpenLodging} />
                      </Grid>
                    )
                  }
                </Grid>
              </Grid>
              <Grid
                item
                xs={1}
                align="right"
              >
                <IconButton
                  aria-label="horse profile settings"
                  onClick={handleMenuClick}
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  id="horse-profile-menu"
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleCloseMenu}
                >
                  <MenuItem onClick={() => { setOpenEditMode(true); }}>
                    <EditIcon color="secondary" sx={{ mr: 2 }} /> Edit Profile Fields
                  </MenuItem>
                  <MenuItem onClick={() => { setTab(4); }}>
                    <LocationOn color="secondary" sx={{ mr: 2 }} /> Manage Horse Location
                  </MenuItem>
                  {(permissions.owner || permissions.admin) && (
                    <MenuItem onClick={() => { setTab(5) }}>
                      <Group color="secondary" sx={{ mr: 2 }} /> Manage Owners & Users
                    </MenuItem>
                  )}
                  <Divider />
                  {currentHorseLodging.inactive === false && (
                    <MenuItem onClick={() => { setDefaultOpenLodging(true); setAnchorEl(null); }}>
                      <Block color="error" sx={{ mr: 2 }} /> Set Horse INACTIVE
                    </MenuItem>
                  )}
                </Menu>
              </Grid>
              {
                (!isFetchingHorse && !isFetchingHorseLodgings && (!horse || !horse.id)) && (
                  <Grid item xs={12}>
                    <Alert severity="warning">
                      Horse information not loaded correctly, you might have issues updating horse information.
                      Try refreshing your browser window, if this warning persists, contact support.
                    </Alert>
                  </Grid>
                )
              }
            </Grid>
          </Paper>
        </Box>
      )
  );
};

ProfileCardComponent.propTypes = {
  id: PropTypes.string.isRequired,
  openAddEditNoteModal: PropTypes.func.isRequired,
  currentHorseLocations: PropTypes.array.isRequired,
  horseOwners: PropTypes.array.isRequired,
  setTab: PropTypes.func,
  setOpenEditMode: PropTypes.func,
  condensed: PropTypes.bool,
};

ProfileCardComponent.defaultProps = {
  setTab: null,
  setOpenEditMode: () => { },
  condensed: false,
}
export default ProfileCardComponent;
